import React, { FC, ReactNode, useReducer, useState, useEffect } from 'react';

import styled from '@emotion/styled';
import { useSiteMetadata, isBrowser, ExtendedDrupalNode } from '@lucid/utils';
import { Global, css } from '@emotion/react';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import { Helmet } from 'react-helmet';
import { Drupal_Metatag, Maybe } from 'generated/drupal-graphql';
import { languages } from '@lucid/gatsby-plugin-i18n';
import {
  Box,
  FontLoader,
  LayoutContext,
  Pingdom,
  SEO,
  ThemeProvider,
  SiteFooter,
  SiteFooterOptionalProps,
  SiteFooterSocialLinks,
  SiteNavbarOptionalProps
} from '@lucid/components-react';
import { theme, LucidTheme } from '@lucid/theme';

import AbTestAdminUi from './ab-test-admin-ui/ab-test-admin-ui';
import { useMenuFooter } from 'hooks/use-menu';
import logo from 'assets/svgs/LucidSoftwareLogo.svg';
import Navbar from 'components/navbar/navbar';

interface ABTest {
  [key: string]: string;
}

interface AbTestObj {
  [key: string]: ABTest;
}

interface LayoutProps {
  langPrefix: string;
  entityTranslations?: ExtendedDrupalNode[];
  metatags?: Array<Maybe<Drupal_Metatag>> | null;
  location: {
    pathname: string;
    origin: string;
  };
  pageType: string;
  children: ReactNode;
  nid?: string;
  shouldHideSsr?: boolean;
  navbarProps?: SiteNavbarOptionalProps;
  footerProps?: SiteFooterOptionalProps;
  hasFooter?: boolean | null;
  hasNavbar?: boolean | null;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<LayoutProps> = ({
  langPrefix,
  location,
  pageType,
  children,
  shouldHideSsr,
  metatags,
  entityTranslations = [],
  nid = '',
  navbarProps,
  footerProps,
  hasNavbar = true,
  hasFooter = true
}) => {
  const {
    allFooterSocialIconsJson,
    i18nData,
    optimizelyHash: { hash }
  } = useStaticQuery(
    graphql`
      {
        allFooterSocialIconsJson {
          edges {
            node {
              name
              logo
              url
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_menu
                mktg_footer_about_privacy
                mktg_footer_legal
              }
            }
          }
        }
        optimizelyHash {
          hash
        }
      }
    `
  );

  const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
  const [shouldShowABTestButtons, setShouldShowABTestButtons] = useState(false);
  const [hasVisibility, setHasVisibility] = useState(!shouldHideSsr);

  const [abTests, updateTest] = useReducer(
    (tempAbTests: AbTestObj, abTest: ABTest) => {
      const theseAbTests = { ...tempAbTests };
      const { test } = abTest;
      const currentTest = tempAbTests[test] || {};

      Object.keys(abTest).forEach(key => {
        currentTest[key] = abTest[key];
      });

      theseAbTests[test] = currentTest;

      return theseAbTests;
    },
    {}
  );

  useEffect(() => {
    setShouldShowEditButton(window.localStorage.getItem('showEditLink') === '1');
    setShouldShowABTestButtons(
      window.localStorage.getItem('showAbTestingUi') === '1'
    );
  }, []);

  useEffect(() => {
    const mobileThreshold = 768;
    const mobileTimeout = 4000;
    const desktopTimeout = 1000;
    const timeoutInMs =
      window.innerWidth >= mobileThreshold ? desktopTimeout : mobileTimeout;
    let safetyTimeoutId: number;

    if (shouldHideSsr) {
      const state = window.optimizely?.get('state');
      const activeExperiments = state?.getActiveExperimentIds() || [];

      if (activeExperiments?.length === 0 || window.optimizelyActionApplied) {
        setHasVisibility(true);
      } else {
        safetyTimeoutId = window.setTimeout(() => {
          setHasVisibility(true);
        }, timeoutInMs);
        Object.defineProperty(window, 'optimizelyActionApplied', {
          set(value) {
            if (value) {
              window.clearTimeout(safetyTimeoutId);
              setHasVisibility(true);
            }
          },
          configurable: true
        });
      }
    }

    return () => {
      window.clearTimeout(safetyTimeoutId);
      Object.defineProperty(window, 'optimizelyActionApplied', {
        value: false,
        writable: true,
        configurable: true
      });
    };
  }, [shouldHideSsr]);

  const { title, cmsUrl } = useSiteMetadata();
  const { shortLangPrefix, i18nKey, urlLangPrefix } = languages[langPrefix];

  const layoutContext = {
    language: {
      langPrefix,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey
    },
    location,
    pageType,
    abTesting: {
      updateTest
    }
  };

  const optimizelyScript = (
    <script defer={!shouldHideSsr} src={withPrefix(`/optimizely-${hash}.js`)} />
  );

  // Footer props
  const footerMenu = useMenuFooter(langPrefix);
  const socialLinks = allFooterSocialIconsJson.edges as SiteFooterSocialLinks[];

  const SiteLogo = (
    <img
      alt={title}
      css={{
        width: '170px',
        maxHeight: '25px',
        position: 'relative',
        top: '2px',
        left: '-1px'
      }}
      src={logo}
    />
  );

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      {/* There might be a better way to include these global settings but this works for now */}
      <Global
        styles={css({
          'html, body, input, textarea, button': {
            fontFamily: 'Graphik, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'
          },
          'html, body': {
            margin: 0,
            padding: 0
          },
          'ul, ol': {
            padding: 0,
            marginLeft: '20px'
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box'
          },
          img: {
            maxWidth: '100%'
          },
          fieldset: {
            border: 'none'
          }
        })}
      />
      <FontLoader />
      <Pingdom />
      <SEO
        entityMetatags={metatags as Drupal_Metatag[]}
        location={location}
        pageType={pageType}
        shortLangPrefix={shortLangPrefix}
      />
      <ThemeProvider product="co" theme={theme}>
        {hasNavbar && (
          <Navbar
            {...navbarProps}
            hasShadow
            isSticky
            i18nData={i18nData}
            langPrefix={langPrefix}
            logo={SiteLogo}
          />
        )}
        <Box
          className="gatsby-root"
          css={{
            visibility: hasVisibility ? 'visible' : 'hidden',
            overflow: 'hidden'
          }}
          data-drupal-nid={nid}
        >
          {children}
          {shouldShowEditButton && (
            <a
              css={(theme: LucidTheme) => ({
                position: 'fixed',
                bottom: '2rem',
                right: '2rem',
                border: `1px solid ${theme.colors.white}`,
                padding: '0.5rem 3rem',
                borderRadius: '0.5rem',
                zIndex: theme.zIndices.zMax,
                background: theme.colors.coolGrey[90],
                color: theme.colors.white,
                transition: 'opacity 0.25s ease',
                opacity: 0.4,
                '&:hover': {
                  opacity: 1
                }
              })}
              href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`}
            >
              Edit Page
            </a>
          )}
          {shouldShowABTestButtons && Object.keys(abTests).length > 0 && (
            <AbTestAdminUi abTests={abTests as any} />
          )}
        </Box>
        {hasFooter && (
          <SiteFooter
            {...footerProps}
            entityTranslations={entityTranslations}
            i18nData={i18nData}
            menu={footerMenu}
            socialLinks={socialLinks}
          />
        )}
      </ThemeProvider>
      {!isBrowser() &&
        (!shouldHideSsr ? (
          <Helmet key={`${pageType}-optimizelyScript`}>
            {optimizelyScript}
          </Helmet>
        ) : (
          <Helmet key={`${pageType}-optimizelyScript`}>
            {optimizelyScript}
          </Helmet>
        ))}
    </EmotionLayoutContextProvider>
  );
};

export default Layout;

export const query = graphql`
  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
  }
`;
